import { useEffect } from "react";

export type ModalTone = "info" | "ok" | "warn" | "error";

export interface ModalProps {
  open: boolean;
  tone?: ModalTone;
  title: string;
  body?: React.ReactNode;
  primaryLabel?: string;
  secondaryLabel?: string;
  onPrimary?: () => void;
  onSecondary?: () => void;
  onClose?: () => void;
}

/**
 * Minimal blocking modal — no external deps. Used for review-page success,
 * validation errors, and "already actioned" states. Closes on Escape and on
 * backdrop click (calls onClose if provided, else onPrimary).
 */
export function Modal({
  open,
  tone = "info",
  title,
  body,
  primaryLabel = "OK",
  secondaryLabel,
  onPrimary,
  onSecondary,
  onClose,
}: ModalProps) {
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") (onClose ?? onPrimary)?.();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, onPrimary]);

  if (!open) return null;

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={(e) => {
        if (e.target === e.currentTarget) (onClose ?? onPrimary)?.();
      }}
    >
      <div className={`modal-shell modal-${tone}`}>
        <div className="modal-icon">
          {tone === "ok"    ? "✅"
           : tone === "warn"  ? "⚠️"
           : tone === "error" ? "⛔"
                              : "ℹ️"}
        </div>
        <h2 className="modal-title">{title}</h2>
        {body && <div className="modal-body">{body}</div>}
        <div className="modal-actions">
          {secondaryLabel && (
            <button type="button" className="modal-btn secondary" onClick={onSecondary}>
              {secondaryLabel}
            </button>
          )}
          <button type="button" className={`modal-btn primary ${tone}`} onClick={onPrimary} autoFocus>
            {primaryLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
